/**
 * Merry's measurement eligibility, ported from Merry's `MetadataController._resolveIsMeasureable`
 * (libraries/metadata-writer, electron/metadata.controller.ts with jpeg-segment-utils.ts; the UPI codes in
 * libraries/merry-common) and kept in step with it: the measure engine opens a capture when the payload of a JPEG APP
 * segment starts with one of the proprietary Evident/Olympus stereo markers (UPI codes). "OLYMPUS IV RAW " is enough;
 * "EVIDENT IV RAW " also needs the stereo sampling type byte in the EXIF UserComment to say the image is reconstructable.
 * Merry's tip adapter, print screen and zoom tags play no part.
 *
 * Merry's code reads Node file handles and packages ViSOL does not depend on, so this works on the file bytes instead, in
 * the browser and in Electron alike. Only JPEG captures are handled. `MerryImageMetadata.cs` in the data management API
 * is the counterpart for the web and the online modes.
 */
export class MerryMeasurable {
  /** Identifiers the measure engine looks for at the start of an APP segment payload; the trailing space is part of each. */
  private static readonly UPI_OLYMPUS_IV_RAW = "OLYMPUS IV RAW ";
  private static readonly UPI_EVIDENT_IV_RAW = "EVIDENT IV RAW ";
  private static readonly UPI_EVIDENT_IV_TEX = "EVIDENT IV TEX ";
  private static readonly UPI_CODES = [MerryMeasurable.UPI_EVIDENT_IV_RAW, MerryMeasurable.UPI_OLYMPUS_IV_RAW, MerryMeasurable.UPI_EVIDENT_IV_TEX];

  /** Merry stops collecting APP payloads at this many bytes, and looks for the sampling type in this much of the file. */
  private static readonly SCAN_BYTES = 256 * 1024;

  private static readonly JPEG_SOI = 0xffd8;
  private static readonly JPEG_SOS = 0xffda;
  private static readonly JPEG_EOI = 0xffd9;
  private static readonly JPEG_APP0 = 0xffe0;
  private static readonly JPEG_APP1 = 0xffe1;
  private static readonly JPEG_APP15 = 0xffef;
  private static readonly XMP_SIGNATURES = ["http://ns.adobe.com/xap/1.0/\0", "http://ns.adobe.com/xmp/extension/\0"];

  /** Whether Merry lets the measure engine open the capture. A file that cannot be walked is not measurable, as in Merry. */
  static isMeasurable(file: ArrayBuffer): boolean {
    try {
      const bytes = new Uint8Array(file);
      const codes = new Set(MerryMeasurable.readAppSegments(bytes, MerryMeasurable.SCAN_BYTES).map((payload) => MerryMeasurable.upiCodeAt(payload)));

      if (codes.has(MerryMeasurable.UPI_OLYMPUS_IV_RAW)) {
        return true;
      }
      if (codes.has(MerryMeasurable.UPI_EVIDENT_IV_RAW)) {
        return MerryMeasurable.isEvidentStereoReconstructable(bytes.subarray(0, MerryMeasurable.SCAN_BYTES));
      }
      return false;
    } catch (error) {
      console.warn("[Merry] could not look for the measure engine markers, the image is stored as not measurable:", error);
      return false;
    }
  }

  /**
   * Payloads of a JPEG's APP segments in file order, without its XMP ones, until `maxBytes` of payload were collected
   * (Merry's JpegSegmentUtils.readAppSegments). Nothing for a file that is not a JPEG.
   */
  private static readAppSegments(bytes: Uint8Array, maxBytes: number): Uint8Array[] {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (bytes.length < 2 || view.getUint16(0) !== MerryMeasurable.JPEG_SOI) {
      return [];
    }

    const segments: Uint8Array[] = [];
    let collected = 0;
    let offset = 2;
    while (collected < maxBytes && offset + 4 <= bytes.length) {
      const marker = view.getUint16(offset);
      const length = view.getUint16(offset + 2);
      if ((marker & 0xff00) !== 0xff00 || marker === MerryMeasurable.JPEG_SOS || marker === MerryMeasurable.JPEG_EOI || length < 2) {
        break;
      }

      if (marker >= MerryMeasurable.JPEG_APP0 && marker <= MerryMeasurable.JPEG_APP15) {
        const payload = bytes.subarray(offset + 4, offset + 2 + length);
        const isXmp =
          marker === MerryMeasurable.JPEG_APP1 && MerryMeasurable.XMP_SIGNATURES.some((signature) => MerryMeasurable.startsWith(payload, signature));
        if (!isXmp) {
          segments.push(payload);
          collected += payload.length;
        }
      }
      offset += 2 + length;
    }
    return segments;
  }

  /** The UPI code an APP segment payload starts with, if any (Merry's UpiSegmentUtils.codeAt). */
  private static upiCodeAt(payload: Uint8Array): string | undefined {
    return MerryMeasurable.UPI_CODES.find((code) => MerryMeasurable.startsWith(payload, code));
  }

  private static startsWith(bytes: Uint8Array, text: string): boolean {
    if (bytes.length < text.length) {
      return false;
    }
    for (let i = 0; i < text.length; i++) {
      if (bytes[i] !== text.charCodeAt(i)) {
        return false;
      }
    }
    return true;
  }

  /**
   * The Evident stereo sampling type byte, at offset 392 of the EXIF UserComment value (MeasureEngineJPGReader.cpp): only
   * 2 (Simultaneous) and 4 (TimeDivision) mean the image can be reconstructed.
   */
  private static isEvidentStereoReconstructable(head: Uint8Array): boolean {
    const userCommentOffset = MerryMeasurable.getExifUserCommentOffset(head);
    if (userCommentOffset === 0) {
      return false;
    }
    const samplingType = head[userCommentOffset + 392];
    return samplingType === 2 || samplingType === 4;
  }

  /**
   * Offset of the EXIF UserComment value in a JPEG whose APP1 directly follows the SOI marker (TIFF header at byte 12):
   * IFD0, then the Exif IFD (tag 0x8769), then UserComment (tag 0x9286). 0 when it cannot be found.
   */
  private static getExifUserCommentOffset(data: Uint8Array): number {
    const TIFF_START = 12;
    const TAG_SIZE = 12;
    const TAG_EXIF_IFD = 0x8769;
    const TAG_USER_COMMENT = 0x9286;

    if (data.length < TIFF_START + 8) {
      return 0;
    }
    const isLittleEndian = data[TIFF_START] === 0x49 && data[TIFF_START + 1] === 0x49; // "II"
    const isBigEndian = data[TIFF_START] === 0x4d && data[TIFF_START + 1] === 0x4d; // "MM"
    if (!isLittleEndian && !isBigEndian) {
      return 0;
    }

    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    const u16 = (offset: number): number => view.getUint16(offset, isLittleEndian);
    const u32 = (offset: number): number => view.getUint32(offset, isLittleEndian);
    const findTag = (ifdStart: number, tagId: number): number => {
      const count = u16(ifdStart);
      for (let i = 0; i < count; i++) {
        const entry = ifdStart + 2 + i * TAG_SIZE;
        if (u16(entry) === tagId) {
          return u32(entry + 8) + TIFF_START;
        }
      }
      return 0;
    };

    const ifd0 = u32(TIFF_START + 4) + TIFF_START;
    const exifIfd = findTag(ifd0, TAG_EXIF_IFD);
    return exifIfd === 0 ? 0 : findTag(exifIfd, TAG_USER_COMMENT);
  }
}
